import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  serviceDetailAPI,
  feesAPI,
  nationalitiesAPI
} from '@/utils/api';

const TABS = [
  { key: 'information', label: 'Information' },
  { key: 'how_to_apply', label: 'How To Apply' },
  { key: 'documents', label: 'Document Checklist' },
  { key: 'faqs', label: "FAQ's" },
  { key: 'fees', label: 'Fees' }
];

const ServiceDetail = () => {
  const { serviceId } = useParams();

  const [activeTab, setActiveTab] = useState('information');
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fees related
  const [fees, setFees] = useState([]);
  const [nationalities, setNationalities] = useState([]);
  const [selectedNationalityId, setSelectedNationalityId] = useState('');
  const [passengers, setPassengers] = useState(1);

  useEffect(() => {
    loadService();
  }, [serviceId]);

  useEffect(() => {
    if (activeTab === 'fees') {
      loadFeesData();
    }
  }, [activeTab]);

  const loadService = async () => {
    try {
      const res = await serviceDetailAPI.getOne(serviceId);
      setService(res.data);
    } catch (err) {
      console.error('Failed to load service', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFeesData = async () => {
    try {
      const [natRes, defaultNatRes] = await Promise.all([
        nationalitiesAPI.getAll(false),
        nationalitiesAPI.getDefault()
      ]);

      setNationalities(natRes.data);

      if (defaultNatRes?.data?.id) {
        setSelectedNationalityId(defaultNatRes.data.id);
        await loadFees(defaultNatRes.data.id);
      }
    } catch (err) {
      console.error('Failed to load fees data', err);
    }
  };

  const loadFees = async (nationalityId) => {
    try {
      const res = await feesAPI.getAll({
        nationality_id: nationalityId,
        service_id: serviceId
      });
      setFees(res.data || []);
    } catch (err) {
      console.error('Failed to load fees', err);
      setFees([]);
    }
  };

  const handleNationalityChange = async (id) => {
    setSelectedNationalityId(id);
    await loadFees(id);
  };

  const totalFeePerPerson = fees.reduce(
    (sum, f) => sum + (f.total_fee || 0),
    0
  );

  const grandTotal = totalFeePerPerson * passengers;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Service not found
      </div>
    );
  }

  const content = service.content || {};

  return (
    <div className="min-h-screen bg-slate-50">

      {/* Hero */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <h1 className="text-4xl font-bold">{service.name}</h1>
          {service.description && (
            <p className="mt-4 max-w-2xl text-slate-200">
              {service.description}
            </p>
          )}
        </div>
      </div>

      {/* Tabs + Content */}
      <div className="max-w-7xl mx-auto px-6 -mt-10 pb-16">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 border-b bg-slate-50 px-4 py-4">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'px-5 py-2.5 text-sm font-medium rounded-full transition-all',
                  activeTab === tab.key
                    ? 'bg-primary text-white shadow'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-8">
            {activeTab !== 'fees' && (
              content[activeTab] ? (
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: content[activeTab] }}
                />
              ) : (
                <p className="text-slate-500">Content not available.</p>
              )
            )}

            {activeTab === 'fees' && (
              <div className="space-y-8">

              {/* Fees List */}
              <div>
                <h3 className="text-xl font-semibold mb-4">
                  Applicable Fees (Per Passenger)
                </h3>

                {fees.length === 0 ? (
                  <p className="text-slate-500">
                    No fee configuration available for this nationality.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {fees.map(fee => {
                      const consular = Number(fee.fee_components?.consular_fee ?? 0);
                      const icwf = Number(fee.fee_components?.icwf_fee ?? 0);
                      const service = Number(fee.fee_components?.service_fee ?? 0);
                      const total = consular + icwf + service;

                      return (
                        <div
                          key={fee.id}
                          className="border rounded-lg p-4 bg-white space-y-2"
                        >
                          <div className="flex justify-between text-sm">
                            <span>Consular Fee</span>
                            <span>{fee.currency} {consular.toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between text-sm">
                            <span>ICWF Fee</span>
                            <span>{fee.currency} {icwf.toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between text-sm">
                            <span>Service Fee</span>
                            <span>{fee.currency} {service.toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between font-semibold border-t pt-2 mt-2">
                            <span>Total (Per Passenger)</span>
                            <span>{fee.currency} {total.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Fee Calculator */}
              <div className="border rounded-xl p-6 bg-slate-50">
                <h3 className="text-lg font-semibold mb-4">
                  Fee Calculator
                </h3>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium">
                      Nationality
                    </label>
                    <select
                      value={selectedNationalityId}
                      onChange={(e) => handleNationalityChange(e.target.value)}
                      className="w-full mt-1 border rounded-md px-3 py-2"
                    >
                      {nationalities.map(n => (
                        <option key={n.id} value={n.id}>
                          {n.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium">
                      Passengers
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={passengers}
                      onChange={(e) =>
                        setPassengers(Number(e.target.value) || 1)
                      }
                      className="w-full mt-1 border rounded-md px-3 py-2"
                    />
                  </div>

                  <div className="flex items-end">
                    <div className="w-full bg-white border rounded-md px-4 py-3">
                      <p className="text-sm text-slate-500">
                        Total Amount
                      </p>
                      <p className="text-xl font-bold">
                        {fees[0]?.currency || ''}{' '}
                        {(
                          fees.reduce((sum, f) => {
                            const c = Number(f.fee_components?.consular_fee ?? 0);
                            const i = Number(f.fee_components?.icwf_fee ?? 0);
                            const s = Number(f.fee_components?.service_fee ?? 0);
                            return sum + (c + i + s);
                          }, 0) * passengers
                        ).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;